import { Badge, Breadcrumbs, Divider, Link, Typography, useMediaQuery } from '@mui/material';
import Holidays from 'date-holidays';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useBoolean } from '../../hooks/useBoolean';
import { useTheme } from '../../hooks/useTheme';
import { capitalizeEveryFirstWordLetter, capitalizeFirstLetter } from '../../utils/capitalize';
import CalendarModal from '../modals/CalendarModal';
import CalendarRangeModal from '../modals/CalendarRangeModal';
import Icon from '../ui/Icon';
import IconButton from '../ui/IconButton';
import { getNextWorkingDay } from '../../utils/dates';

const hd = new Holidays('FR');

interface HeaderProps {
    title: string;
    subTitle?: string;
    breadcrumbs?: { label: string; link?: string }[];
    children?: React.ReactNode;
    isDateVisible?: boolean;
    isSecondDateVisible?: boolean;
    initialDate?: Date;
    initialSecondDate?: Date;
    onDateChange?: (newDate: Date) => void;
    onSecondDateChange?: (newSecondDate: Date) => void;
    shortDate?: boolean;
    shortSecondDate?: boolean;
    mode?: 'day' | 'month';
}

const Header: React.FC<HeaderProps> = ({
    title,
    subTitle,
    breadcrumbs,
    children,
    initialDate,
    isDateVisible = true,
    isSecondDateVisible = false,
    onDateChange = () => {},
    onSecondDateChange = () => {},
    initialSecondDate,
    shortDate = false,
    shortSecondDate = false,
    mode = 'day',
}) => {
    const { t } = useTranslation();
    const { muiTheme } = useTheme();
    const [date, setDate] = useState<Date>(initialDate || new Date());
    const [secondDate, setSecondDate] = useState<Date>(initialSecondDate || new Date());
    const [showCalendarModal, setShowCalendarModal] = useState(false);

    const calendarModal = useBoolean(false);

    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

    const sameDay = (d1: Date, d2: Date) => {
        return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            onDateChange(date);
        }, 300);

        return () => clearTimeout(timeout);
    }, [date]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onSecondDateChange(secondDate);
        }, 300);

        return () => clearTimeout(timeout);
    }, [secondDate]);

    const handleLeftArrow = () => {
        const newDate = new Date(date);
        if (mode === 'month') {
            newDate.setMonth(newDate.getMonth() - 1);
        } else {
            newDate.setDate(newDate.getDate() - 1);
        }
        setDate(newDate);
    };

    const handleRightArrow = () => {
        const newDate = new Date(date);
        if (mode === 'month') {
            newDate.setMonth(newDate.getMonth() + 1);
        } else {
            newDate.setDate(newDate.getDate() + 1);
        }
        setDate(newDate);
    };

    const handleSecondLeftArrow = () => {
        const newDate = new Date(secondDate);
        newDate.setDate(newDate.getDate() - 1);
        setSecondDate(newDate);
    };

    const handleSecondRightArrow = () => {
        const newDate = new Date(secondDate);
        newDate.setDate(newDate.getDate() + 1);
        setSecondDate(newDate);
    };

    const formatDate = (dateToFormat: Date, isShort: boolean) => {
        return capitalizeEveryFirstWordLetter(
            dateToFormat.toLocaleDateString(t('localeDate'), {
                year: 'numeric',
                month: 'short',
                ...(mode === 'day' && {
                    day: 'numeric',
                    weekday: 'short',
                }),
            })
        );
    };

    const renderDateDisplay = (displayDate: Date, isShort: boolean) => (
        <Badge variant="dot" color="info" invisible={!sameDay(displayDate, new Date())}>
            <div
                className={'plan-date fx-r fx-nw fx-justify-c ' + (isMobile ? 'mdFont' : isTablet ? 'lgFont' : 'xxlFont')}
                style={{
                    color:
                        mode === 'day'
                            ? hd.isHoliday(displayDate)
                                ? muiTheme.palette.error.main
                                : displayDate.getDay() === 6
                                  ? muiTheme.palette.warning.main
                                  : displayDate.getDay() === 0
                                    ? muiTheme.palette.error.main
                                    : undefined
                            : undefined,
                    padding: '0px 5px',
                }}
            >
                {formatDate(displayDate, isShort)}
            </div>
        </Badge>
    );

    return (
        <div
            className="w-100 pad-10px border-radius-10px"
            style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? '15px' : '10px',
                flexWrap: isTablet && !isMobile ? 'wrap' : 'nowrap',
            }}
        >
            {/* Section Titre et Breadcrumbs */}
            <div
                style={{
                    flex: isMobile ? 'none' : isTablet ? '1 1 100%' : '1 1 33%',
                    minWidth: 0, // Important pour permettre au texte de se réduire
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}
                className="fx-r fx-items-bl font-w-700 gap-15px"
            >
                <Typography variant={isMobile ? 'h6' : isTablet ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 700 }}>
                    {title.toUpperCase()}
                </Typography>
                <Breadcrumbs
                    separator={<Icon name="navigateNext" />}
                    sx={{
                        '& .MuiBreadcrumbs-ol': {
                            flexWrap: 'wrap',
                        },
                    }}
                >
                    {breadcrumbs?.map((bc, i) => {
                        const isLast = i === breadcrumbs.length - 1;
                        return isLast ? (
                            <Typography key={i} color="text.primary" sx={{ fontSize: isTablet ? '0.875rem' : '1rem' }}>
                                {bc.label}
                            </Typography>
                        ) : (
                            <Link
                                key={i}
                                component={RouterLink}
                                to={bc.link || '#'}
                                underline="hover"
                                color="inherit"
                                sx={{ fontSize: isTablet ? '0.875rem' : '1rem' }}
                            >
                                {bc.label}
                            </Link>
                        );
                    })}
                </Breadcrumbs>
            </div>

            {/* Section Dates */}
            <div
                style={{
                    flex: isMobile ? 'none' : isTablet ? '1 1 100%' : '1 1 33%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}
            >
                {isDateVisible && (
                    <div
                        className={'fx-r fx-items-c border-radius-10px ' + (isSecondDateVisible ? 'fx-justify-fe' : 'fx-justify-sb')}
                        style={{
                            minWidth: isMobile ? '100%' : isTablet ? '200px' : '300px',
                            gap: '5px',
                        }}
                    >
                        {!isSecondDateVisible && (
                            <IconButton
                                iconName="arrowBack"
                                onClick={handleLeftArrow}
                                title={capitalizeFirstLetter(t('previousDay'))}
                                size={isMobile ? 'small' : 'medium'}
                            />
                        )}
                        {renderDateDisplay(date, shortDate)}
                        {!isSecondDateVisible && (
                            <IconButton
                                iconName="arrowForward"
                                onClick={handleRightArrow}
                                title={capitalizeFirstLetter(t('nextDay'))}
                                disabled={isSecondDateVisible && sameDay(date, secondDate)}
                                size={isMobile ? 'small' : 'medium'}
                            />
                        )}
                    </div>
                )}

                {isDateVisible && isSecondDateVisible && (
                    <div className="text-gray" style={{ padding: '0 5px' }}>
                        {'➔'}
                    </div>
                )}

                {isSecondDateVisible && (
                    <div
                        className={'fx-r fx-items-c fx-justify-sb border-radius-10px'}
                        style={{
                            minWidth: isMobile ? '100%' : isTablet ? '200px' : '300px',
                            gap: '5px',
                        }}
                    >
                        {!isDateVisible && (
                            <IconButton
                                iconName="arrowBack"
                                onClick={handleSecondLeftArrow}
                                title={capitalizeFirstLetter(t('previousDay'))}
                                disabled={sameDay(date, secondDate)}
                                size={isMobile ? 'small' : 'medium'}
                            />
                        )}
                        {renderDateDisplay(secondDate, shortSecondDate)}
                        {!isDateVisible && (
                            <IconButton
                                iconName="arrowForward"
                                onClick={handleSecondRightArrow}
                                title={capitalizeFirstLetter(t('nextDay'))}
                                size={isMobile ? 'small' : 'medium'}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Section Actions */}
            <div
                style={{
                    flex: isMobile ? 'none' : isTablet ? '1 1 100%' : '1 1 33%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: isMobile ? 'center' : isTablet ? 'center' : 'flex-end',
                    alignItems: 'center',
                    gap: isMobile ? '10px' : '20px',
                    flexWrap: 'wrap',
                }}
            >
                {initialDate && !initialSecondDate && (
                    <IconButton
                        iconName="today"
                        onClick={() => setDate(new Date())}
                        title={capitalizeFirstLetter(t('today'))}
                        size={isMobile ? 'small' : 'medium'}
                    />
                )}
                {initialDate && !initialSecondDate && mode === 'day' && (
                    <IconButton
                        iconName="event"
                        onClick={() => setDate(getNextWorkingDay(new Date()))}
                        title={capitalizeFirstLetter(t('nextWorkingDay'))}
                        size={isMobile ? 'small' : 'medium'}
                    />
                )}

                {initialDate && !initialSecondDate && (
                    <IconButton
                        iconName="calendarMonth"
                        onClick={() => calendarModal.setTrue()}
                        title={capitalizeFirstLetter(t('calendar'))}
                        size={isMobile ? 'small' : 'medium'}
                    />
                )}
                {initialDate && initialSecondDate && (
                    <IconButton
                        iconName="calendarMonth"
                        onClick={() => setShowCalendarModal(true)}
                        title={capitalizeFirstLetter(t('calendar'))}
                        size={isMobile ? 'small' : 'medium'}
                    />
                )}
                {children && !isMobile && initialDate && <Divider orientation="vertical" flexItem variant="middle" />}
                {children}
            </div>

            {calendarModal.value && (
                <CalendarModal
                    currentDate={date}
                    setCurrentDate={(newDate) => setDate(new Date(newDate))}
                    mode={mode}
                    open={calendarModal.value}
                    handleClose={calendarModal.setFalse}
                />
            )}

            {showCalendarModal && (
                <CalendarRangeModal
                    startDate={date}
                    endDate={secondDate}
                    setStartDate={(newDate) => {
                        setDate(new Date(newDate));
                    }}
                    setEndDate={(newDate) => setSecondDate(new Date(newDate))}
                    open={showCalendarModal}
                    handleClose={() => setShowCalendarModal(false)}
                />
            )}
        </div>
    );
};

export default Header;
